import {
  getContractFilesForContract,
  createContractManagementContractFile,
  updateContractManagementContractFile,
  deleteContractManagementContractFile,
  moveContractManagementContractFile,
  searchContractManagementContractFiles,
  removeObligationFromContractFile,
  removeClauseFromContractFile,
  addObligationToContractFile,
  addClauseToContractFile,
  type FilterableSearchQuery,
  type ContractManagementContractFileSearchResult,
  type ContractManagementContractFileUpdateUser,
  type ContractManagementContractFileInDb,
  type ContractManagementContractFileMoveUser,
  type ContractManagementObligationInDb,
  type ContractManagementClauseInDb,
} from '@/api';
import { logger } from '@/utils/logger';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export const ContractFilesService = {
  /**
   * Get contract files for a specific contract with pagination and filtering
   */
  async getContractFilesForContract(
    contractUid: string,
    query: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
      columnFilters?: Record<string, string[]>;
    } = {}
  ): Promise<ContractManagementContractFileSearchResult> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.pageSize || 10,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.pageSize || 10) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query.sortDirection || 'desc',
        similarity_threshold: 0.5,
        column_filters: query.columnFilters ?? {},
      };

      const response = await getContractFilesForContract({
        path: { uid: contractUid },
        body: searchQuery,
      });

      if (!response.data) {
        throw new Error('Failed to fetch contract files for contract');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to fetch contract files for contract. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  /**
   * Every file of a contract, oldest first. The search caps a page at 50, so
   * this pages through until it has them all.
   */
  async getAllContractFilesForContract(
    contractUid: string
  ): Promise<ContractManagementContractFileInDb[]> {
    const pageSize = 50;
    const files: ContractManagementContractFileInDb[] = [];
    for (let page = 1; ; page++) {
      const result = await this.getContractFilesForContract(contractUid, {
        page,
        pageSize,
        sortKey: 'created_at',
        sortDirection: 'asc',
      });
      files.push(...result.results);
      if (result.results.length < pageSize || files.length >= result.result_count) return files;
    }
  },

  /**
   * File a plain vault file into a contract; it leaves its folder. The AI fills
   * in the new contract file's type and description. Needs ADMIN on the file
   * and WRITE on the contract; a file already in a contract is a 409.
   */
  async createContractFile(
    contractUid: string,
    vaultFileUid: string
  ): Promise<ContractManagementContractFileInDb> {
    try {
      const response = await createContractManagementContractFile({
        body: { vault_file_uid: vaultFileUid },
        query: { contract_uid: contractUid },
        throwOnError: true,
      });
      return response.data;
    } catch (error) {
      logger.error('Error creating contract file:', error);
      throw new Error(
        extractErrorMessage(error, 'Failed to add the file to the contract. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Update an existing contract file
   */
  async update(
    uid: string,
    updateData: ContractManagementContractFileUpdateUser
  ): Promise<ContractManagementContractFileInDb> {
    try {
      const response = await updateContractManagementContractFile({
        path: { uid },
        body: updateData,
      });

      if (!response.data) {
        throw new Error('Failed to update contract file');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Move a contract file to another contract (existing or newly created).
   * @param contractFileUid - UID of the contract file to move
   * @param moveData - Either target_contract_uid (existing) or new_contract (create next to the source and move)
   * @returns The updated contract file
   */
  async move(
    contractFileUid: string,
    moveData: ContractManagementContractFileMoveUser
  ): Promise<ContractManagementContractFileInDb> {
    try {
      const response = await moveContractManagementContractFile({
        path: { uid: contractFileUid },
        body: moveData,
      });

      if (!response.data) {
        throw new Error('Failed to move contract file - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to move contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Delete a contract file
   * @returns The deleted contract file
   */
  async delete(uid: string): Promise<ContractManagementContractFileInDb> {
    try {
      const response = await deleteContractManagementContractFile({
        path: { uid },
      });

      if (!response.data) {
        throw new Error('Failed to delete contract file - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to delete contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Remove an obligation from a contract file
   * @param obligationUid - The UID of the obligation to remove
   * @param contractFileUid - The UID of the contract file
   * @returns The removed obligation
   */
  async removeObligation(
    obligationUid: string,
    contractFileUid: string
  ): Promise<ContractManagementObligationInDb> {
    try {
      const response = await removeObligationFromContractFile({
        query: {
          obligation_uid: obligationUid,
          contract_file_uid: contractFileUid,
        },
      });

      if (!response.data) {
        throw new Error(
          'Failed to remove obligation from contract file - no data returned from API'
        );
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to remove obligation from contract file. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  /**
   * Remove a clause from a contract file
   * @param clauseUid - The UID of the clause to remove
   * @param contractFileUid - The UID of the contract file
   * @returns The removed clause
   */
  async removeClause(
    clauseUid: string,
    contractFileUid: string
  ): Promise<ContractManagementClauseInDb> {
    try {
      const response = await removeClauseFromContractFile({
        query: {
          clause_uid: clauseUid,
          contract_file_uid: contractFileUid,
        },
      });

      if (!response.data) {
        throw new Error('Failed to remove clause from contract file - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to remove clause from contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Add an obligation to a contract file
   * @param obligationUid - The UID of the obligation to add
   * @param contractFileUid - The UID of the contract file
   * @param pageNumber - Optional page number to associate with
   * @returns The obligation
   */
  async addObligation(
    obligationUid: string,
    contractFileUid: string,
    pageNumber: number | null = null
  ): Promise<ContractManagementObligationInDb> {
    try {
      const response = await addObligationToContractFile({
        query: {
          obligation_uid: obligationUid,
          contract_file_uid: contractFileUid,
          page_number: pageNumber,
        },
      });

      if (!response.data) {
        throw new Error('Failed to add obligation to contract file - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to add obligation to contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Add a clause to a contract file
   * @param clauseUid - The UID of the clause to add
   * @param contractFileUid - The UID of the contract file
   * @param pageNumber - Optional page number to associate with
   * @returns The clause
   */
  async addClause(
    clauseUid: string,
    contractFileUid: string,
    pageNumber: number | null = null
  ): Promise<ContractManagementClauseInDb> {
    try {
      const response = await addClauseToContractFile({
        query: {
          clause_uid: clauseUid,
          contract_file_uid: contractFileUid,
          page_number: pageNumber,
        },
      });

      if (!response.data) {
        throw new Error('Failed to add clause to contract file - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to add clause to contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Search all contract files with pagination
   */
  async searchContractFiles(
    query: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
    } = {}
  ): Promise<ContractManagementContractFileSearchResult> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.pageSize || 20,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.pageSize || 20) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query.sortDirection || 'desc',
        similarity_threshold: 0.5,
      };

      const response = await searchContractManagementContractFiles({
        body: searchQuery,
      });

      if (!response.data) {
        throw new Error('Failed to search contract files');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to search contract files. Please try again.'),
        { cause: error }
      );
    }
  },
};
